import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { useAssenzeOperatori } from '../../../data_fetching/UserHours/useAssenzeOperatori';
import { inServizioQueryOptions } from '../../../data_fetching/UserHours/useInServizio';
import { CalendarioUtenteGiorno } from '../../../interfaces/CalendarioUtenteGiorno';
import {
  buildAssenzeOperatori,
  FinestraAssenze,
  getFinestraAssenze,
} from '../utility_objects/AssenzeOperatoriUtils';
import {
  buildPresenzeGiorni,
  PresenzeGiorno,
} from '../utility_objects/PresenzeUtils';

/** caricamento = Skeleton; errore = "Non disponibile" / "Assenze non disponibili". */
export type StatoDati = 'caricamento' | 'errore' | 'pronto';

export interface GiornoFascia extends PresenzeGiorno {
  statoInServizio: StatoDati;
}

export interface PresenzeGiorni {
  /** Un elemento per giorno chiesto, nello stesso ordine. */
  giorni: GiornoFascia[];
  statoAssenze: StatoDati;
  /** Tutte le chiamate in errore: una sola scritta, "Presenze non disponibili". */
  tuttoInErrore: boolean;
}

// Senza giorni la query delle assenze resta spenta: la chiave non conta.
const NESSUNA_FINESTRA: FinestraAssenze = { inizio: '', fine: '' };

// Con dati già letti si continuano a mostrare quelli anche se un aggiornamento in
// background fallisce: la cella non si svuota per un errore passeggero.
const getStato = ({
  data,
  isError,
}: {
  data: unknown;
  isError: boolean;
}): StatoDati => {
  if (data !== undefined) return 'pronto';
  return isError ? 'errore' : 'caricamento';
};

/**
 * Dati della fascia Presenze di un punto vendita per i giorni chiesti, uguali in
 * ogni agenda che la mostra: una query "in servizio" per giorno (react-query
 * deduplica le chiamate fra chi usa l'hook) più le assenze della finestra ancorata
 * alla settimana del primo giorno. Le chiavi sono le stesse ovunque, quindi le pagine
 * condividono la cache. Con `attiva` false non parte nessuna chiamata e le celle
 * restano in caricamento.
 */
export const usePresenzeGiorni = ({
  pointOfSaleId,
  giorni,
  attiva,
}: {
  pointOfSaleId: string;
  /**
   * YYYY-MM-DD, nell'ordine delle celle; vuoto = nessuna cella. Stesso array fra
   * un render e l'altro (useMemo), altrimenti le celle si ricalcolano ogni volta.
   */
  giorni: string[];
  attiva: boolean;
}): PresenzeGiorni => {
  const { users } = useSelector(
    (state: RootState) => state.bluBookingCalendarFilters
  );
  const primoGiorno = giorni[0];
  const finestra = useMemo(
    () =>
      primoGiorno === undefined
        ? NESSUNA_FINESTRA
        : getFinestraAssenze(primoGiorno),
    [primoGiorno]
  );

  const risultatiInServizio = useQueries({
    queries: giorni.map((giorno) =>
      inServizioQueryOptions(pointOfSaleId, giorno, attiva)
    ),
  });
  const assenzeQuery = useAssenzeOperatori({
    pointOfSaleId,
    finestra,
    enabled: attiva && primoGiorno !== undefined,
  });
  const statoAssenze = getStato(assenzeQuery);

  const assenze = useMemo(
    () =>
      assenzeQuery.data
        ? buildAssenzeOperatori(
            assenzeQuery.data.righe,
            assenzeQuery.data.eccezioniNegozio,
            users,
            pointOfSaleId,
            finestra
          )
        : undefined,
    [assenzeQuery.data, users, pointOfSaleId, finestra]
  );

  // useQueries restituisce un array nuovo a ogni render (react-query 4 non ha
  // combine): le celle si ricalcolano solo quando cambia un dato o uno stato.
  const versioneInServizio = risultatiInServizio
    .map((risultato) => `${risultato.dataUpdatedAt}:${getStato(risultato)}`)
    .join('|');

  const giorniFascia = useMemo(
    () => {
      const inServizioPerGiorno: Record<
        string,
        CalendarioUtenteGiorno[] | undefined
      > = {};
      giorni.forEach((giorno, i) => {
        inServizioPerGiorno[giorno] = risultatiInServizio[i]?.data;
      });
      return buildPresenzeGiorni(
        giorni,
        inServizioPerGiorno,
        assenze,
        users
      ).map(
        (presenze, i): GiornoFascia => ({
          ...presenze,
          statoInServizio: risultatiInServizio[i]
            ? getStato(risultatiInServizio[i])
            : 'caricamento',
        })
      );
    },
    // risultatiInServizio è rappresentato da versioneInServizio (vedi sopra).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [giorni, versioneInServizio, assenze, users]
  );

  const tuttoInErrore =
    statoAssenze === 'errore' &&
    giorniFascia.length > 0 &&
    giorniFascia.every((giorno) => giorno.statoInServizio === 'errore');

  return useMemo(
    () => ({ giorni: giorniFascia, statoAssenze, tuttoInErrore }),
    [giorniFascia, statoAssenze, tuttoInErrore]
  );
};
